import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLoadMyReportsQuery, useLoadMyReportsUsageQuery, useRequestClaudeUsageMutation } from '../app/api';
import { useIsMobile } from './utils';
import UsageTierChart, { TierDot, ClaudeCodeMark, USAGE_TIERS, matchesTier, tierForCount } from './UsageTierChart';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

// Cards sit one-per-row but width-capped so they don't stretch across a wide
// monitor (full width looked too sparse; half width felt cramped).
const CARD_MAX_WIDTH = 760;

// Live Claude-account status for the current month (from the backend, which
// uses the Admin API org-member list when configured): does the report have a
// Claude account, and are they using it. On the dark strip we render light text.
const ACCOUNT_META = {
  using:        { label: 'Active',     icon: 'check-circle-fill', color: '#3ecf8e' },
  no_account:   { label: 'No Usage',   icon: 'x-circle',          color: '#f08a7e' },
};

function AccountBadge({ status }) {
  const m = ACCOUNT_META[status];
  if (!m) return null;
  return (
    <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: m.color, whiteSpace: 'nowrap' }}>
      <i className={`bi bi-${m.icon} me-1`} />{m.label}
    </span>
  );
}
AccountBadge.propTypes = { status: PropTypes.string };

// Claude certification status, shown next to the report's name (white card
// header). Paired with the account badge, it disambiguates a $0 row.
const CERT_META = {
  completed:   { label: 'Certified',   icon: 'check-circle-fill', color: brand.green },
  pending:     { label: 'Pending',     icon: 'hourglass-split',   color: brand.goldenrod },
  not_started: { label: 'Not started', icon: 'dash-circle',       color: brand.charcoal },
};

function CertBadge({ status }) {
  const m = CERT_META[status];
  if (!m) return null;
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: m.color, whiteSpace: 'nowrap' }}>
      <i className={`bi bi-${m.icon} me-1`} />{m.label}
    </span>
  );
}
CertBadge.propTypes = { status: PropTypes.string };

// Current month as "YYYY-MM" (UTC, to match the backend's date handling).
function currentMonth() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

const usd = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Tooltip text listing each former/alias address and its spend, one per line.
const formerTitle = (others) =>
  (others || []).map(o => `${o.email}: ${usd(o.cost_usd)}`).join('\n') || 'Spend under former addresses';

// Claude spend strip shown above each report's card. `usage` is the per-person
// month-to-date spend (or undefined if that person had no Claude usage / isn't
// in the Enterprise org).
function UsageStrip({ usage, loading, windowLabel }) {
  const total = usage ? (usage.total_cost_usd ?? usage.cost_usd ?? 0) : 0;
  const priorCost = usage?.prior_cost_usd || 0;
  const activity = usage?.activity_30d;
  return (
    <div style={{
      background: brand.deepBlue, color: 'white',
      padding: '0.5rem 0.85rem',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '0.5rem', flexWrap: 'wrap',
    }}>
      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.goldenrod }}>
          <i className="bi bi-robot me-1" />Claude spend ({windowLabel})
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          {!loading && usage?.account_status && <AccountBadge status={usage.account_status} />}
          {!loading && activity != null && (
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' }} title="Messages across all surfaces + Claude Code sessions (weighted), last 30 days">
              <i className="bi bi-activity me-1" />{Number(activity).toLocaleString()} activity
              <span style={{ opacity: 0.6, margin: '0 0.35rem' }}>~</span>
              <span style={{ color: 'white' }}>{tierForCount(activity).label}</span>
            </span>
          )}
        </span>
      </span>
      {loading ? (
        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>Loading…</span>
      ) : (
        <span style={{ textAlign: 'right' }}>
          <strong style={{ fontSize: '1.05rem' }}>{usd(total)}</strong>
          {/* Distinguish spend that came in under former/alias email addresses,
              so a migrated report's combined total is explainable. Hover for the
              per-address breakdown. */}
          {priorCost > 0 && (
            <span
              style={{ display: 'block', fontSize: '0.68rem', color: 'rgba(255,255,255,0.8)', marginTop: 1, cursor: 'help' }}
              title={formerTitle(usage.other_addresses)}
            >
              {usd(usage.cost_usd)} current · <span style={{ color: brand.goldenrod }}><i className="bi bi-clock-history me-1" />{usd(priorCost)} former{usage.other_addresses?.length > 1 ? ` (${usage.other_addresses.length})` : ''}</span>
            </span>
          )}
        </span>
      )}
    </div>
  );
}
UsageStrip.propTypes = {
  usage: PropTypes.shape({
    cost_usd: PropTypes.number,
    prior_cost_usd: PropTypes.number,
    total_cost_usd: PropTypes.number,
    account_status: PropTypes.string,
    activity_30d: PropTypes.number,
    other_addresses: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string,
      cost_usd: PropTypes.number,
    })),
  }),
  loading: PropTypes.bool,
  windowLabel: PropTypes.string,
};

// "Request more usage" — shown on the caller's own card. Emails the configured
// hub recipients; no in-app queue. Optional target amount + reason.
function RequestUsage() {
  const [request, { isLoading }] = useRequestClaudeUsageMutation();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    const dollars = amount.trim() === '' ? null : parseFloat(amount);
    if (dollars != null && (!Number.isFinite(dollars) || dollars <= 0)) {
      setError('Enter a valid target amount, or leave it blank.');
      return;
    }
    try {
      await request({ requested_usd: dollars, note: note.trim() || null }).unwrap();
      setSent(true); setOpen(false); setAmount(''); setNote('');
    } catch (e) {
      setError(e?.data?.detail || 'Could not send your request. Please try again.');
    }
  };

  if (sent) {
    return (
      <div style={{ padding: '0.65rem 1rem', borderTop: `1px solid ${brand.softGray}`, color: brand.green, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <i className="bi bi-check-circle-fill" /> Request sent — someone will follow up.
      </div>
    );
  }

  return (
    <div style={{ padding: '0.65rem 1rem', borderTop: `1px solid ${brand.softGray}` }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}
          style={{ background: 'transparent', border: `1px solid ${brand.tealBlue}`, color: brand.tealBlue, borderRadius: 8, padding: '0.35rem 0.7rem', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
          <i className="bi bi-plus-circle me-1" />Request more usage
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', color: brand.charcoal }}>Target (optional) $</span>
            <input type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="/ mo"
              style={{ width: 90, border: '1px solid #cdd5dd', borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.85rem' }} />
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why do you need more? (optional)"
            style={{ border: '1px solid #cdd5dd', borderRadius: 6, padding: '0.4rem 0.5rem', fontSize: '0.85rem', resize: 'vertical' }} />
          {error && <div style={{ color: brand.red, fontSize: '0.78rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button type="button" onClick={submit} disabled={isLoading}
              style={{ background: brand.deepBlue, color: 'white', border: 0, borderRadius: 8, padding: '0.35rem 0.8rem', fontSize: '0.8rem', fontWeight: 700, cursor: isLoading ? 'wait' : 'pointer' }}>
              {isLoading ? 'Sending…' : 'Send request'}
            </button>
            <button type="button" onClick={() => { setOpen(false); setError(null); }} disabled={isLoading}
              style={{ background: 'transparent', border: '1px solid #cdd5dd', color: brand.charcoal, borderRadius: 8, padding: '0.35rem 0.7rem', fontSize: '0.8rem', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyTeam() {
  const isMobile = useIsMobile();
  // Default to a trailing 30-day window so a report's usage doesn't reset to $0
  // right after a month rollover; the month picker stays available.
  const [rolling, setRolling] = useState(true);
  const [month, setMonth] = useState(currentMonth);
  const [accountFilter, setAccountFilter] = useState('all');
  const [certFilter, setCertFilter] = useState('all');
  const [firmFilter, setFirmFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [copied, setCopied] = useState(false);
  // The team usage distribution (circle chart) is collapsed by default — it can
  // be long, so a manager opens it on demand.
  const [showChart, setShowChart] = useState(false);

  const { data, isLoading, isError } = useLoadMyReportsQuery();
  const isManager = Boolean(data?.is_manager);
  const reports = data?.reports || [];               // direct reports
  const indirectReports = useMemo(() => data?.indirect || [], [data]); // reports of reports (any depth)
  const me = data?.self || null;

  // Usage covers the caller's own row (always) plus their reporting subtree if
  // they're a manager — the endpoint resolves that itself, so it runs for
  // everyone in the directory.
  const usageQuery = useLoadMyReportsUsageQuery(
    rolling ? {} : { month },
    { skip: !me },
  );
  const usageConfigured = usageQuery.data?.configured;
  const usageByReport = usageQuery.data?.usage || {};
  const usageLoading = usageQuery.isFetching;

  const firmOptions = useMemo(
    () => Array.from(new Set([...reports, ...indirectReports].map(r => r.firm).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [reports, indirectReports],
  );

  // Same firm / account / certification filter applied to both direct and
  // indirect reports.
  const matchesFilters = useCallback((emp) => {
    const u = usageByReport[emp.id];
    if (firmFilter !== 'all' && (emp.firm || '') !== firmFilter) return false;
    if (accountFilter !== 'all' && u?.account_status !== accountFilter) return false;
    if (certFilter !== 'all' && (u?.cert_status || 'not_started') !== certFilter) return false;
    if (rolling && !matchesTier(u?.activity_30d, tierFilter)) return false;
    return true;
  }, [usageByReport, firmFilter, accountFilter, certFilter, tierFilter, rolling]);

  const filteredReports = useMemo(() => reports.filter(matchesFilters), [reports, matchesFilters]);
  const filteredIndirect = useMemo(() => indirectReports.filter(matchesFilters), [indirectReports, matchesFilters]);

  // Indirect reports grouped under whoever they report to (their manager, who
  // is somewhere in the subtree below the current user).
  const indirectGroups = useMemo(() => {
    const map = new Map();
    filteredIndirect.forEach(emp => {
      const key = emp.manager_name || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(emp);
    });
    return Array.from(map, ([manager, members]) => ({ manager, members }));
  }, [filteredIndirect]);

  // "Copy emails" covers the whole visible team (direct + indirect).
  const filteredEmails = useMemo(
    () => Array.from(new Set([...filteredReports, ...filteredIndirect].map(r => (r.final_email || '').trim().toLowerCase()).filter(Boolean))),
    [filteredReports, filteredIndirect],
  );

  const copyEmails = async () => {
    if (!filteredEmails.length) return;
    try {
      await navigator.clipboard.writeText(filteredEmails.join('; '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      window.alert('Could not copy to clipboard.');
    }
  };

  // Month picker bounded to today so managers can't ask for future months.
  const maxMonth = currentMonth();

  // The caller's own "You" card, kept as a card and centred at CARD_MAX_WIDTH.
  // (Reports render in a table below — see renderReportsTable.)
  const renderReportCard = (emp, opts = {}) => (
    <div key={emp.id} className="col-12">
      <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(10,41,71,0.12)', height: '100%', maxWidth: CARD_MAX_WIDTH, margin: opts.isSelf ? '0 auto' : undefined, display: 'flex', flexDirection: 'column', border: opts.isSelf ? `1px solid ${brand.tealBlue}` : 'none' }}>
        <div style={{ padding: '0.85rem 1rem', borderBottom: `1px solid ${brand.softGray}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {!usageLoading && <TierDot count={usageByReport[emp.id]?.activity_30d} />}
              <span style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '1.05rem' }}>
                {emp.first_name} {emp.last_name}
                {!usageLoading && <ClaudeCodeMark show={usageByReport[emp.id]?.uses_claude_code} />}
              </span>
              {opts.isSelf && (
                <span style={{ background: brand.tealBlue, color: 'white', fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0.1rem 0.4rem', borderRadius: 999 }}>
                  You
                </span>
              )}
            </span>
            {emp.firm && (
              <span style={{ color: brand.tealBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>{emp.firm}</span>
            )}
          </span>
          {!usageLoading && usageByReport[emp.id] && (
            <CertBadge status={usageByReport[emp.id].cert_status} />
          )}
        </div>
        <div style={{ marginTop: 'auto' }}>
          <UsageStrip usage={usageByReport[emp.id]} loading={usageLoading} windowLabel={rolling ? 'last 30 days' : 'MTD'} />
          {opts.isSelf && <RequestUsage />}
        </div>
      </div>
    </div>
  );

  // ── Reports table (direct reports + each indirect group) ──────────────────
  const th = { padding: '0.55rem 0.85rem', fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, textAlign: 'left', borderBottom: `2px solid ${brand.softGray}`, whiteSpace: 'nowrap' };
  const td = { padding: '0.6rem 0.85rem', fontSize: '0.85rem', color: brand.charcoal, borderBottom: `1px solid ${brand.softGray}`, verticalAlign: 'middle' };

  const renderReportRow = (emp) => {
    const u = usageByReport[emp.id];
    const total = u ? (u.total_cost_usd ?? u.cost_usd ?? 0) : 0;
    const prior = u?.prior_cost_usd || 0;
    const activity = u?.activity_30d;
    return (
      <tr key={emp.id}>
        <td style={td}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            {!usageLoading && <TierDot count={activity} />}
            <span style={{ color: brand.deepBlue, fontWeight: 700 }}>
              {emp.first_name} {emp.last_name}
              {!usageLoading && <ClaudeCodeMark show={u?.uses_claude_code} />}
            </span>
          </span>
        </td>
        <td style={td}>
          {emp.firm
            ? <span style={{ color: brand.tealBlue, fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{emp.firm}</span>
            : <span style={{ opacity: 0.4 }}>—</span>}
        </td>
        <td style={td}>{!usageLoading && u ? <CertBadge status={u.cert_status} /> : <span style={{ opacity: 0.4 }}>—</span>}</td>
        <td style={td}>
          {!usageLoading && u?.account_status ? (
            <span style={{ fontWeight: 700, fontSize: '0.72rem', color: u.account_status === 'using' ? brand.green : brand.red, whiteSpace: 'nowrap' }}>
              <i className={`bi bi-${u.account_status === 'using' ? 'check-circle-fill' : 'x-circle'} me-1`} />
              {u.account_status === 'using' ? 'Active' : 'No usage'}
            </span>
          ) : <span style={{ opacity: 0.4 }}>—</span>}
        </td>
        {rolling && (
          <td style={td}>
            {!usageLoading && activity != null ? (
              <span style={{ whiteSpace: 'nowrap' }} title="Messages across all surfaces + Claude Code sessions (weighted), last 30 days">
                {Number(activity).toLocaleString()}
                <span style={{ opacity: 0.5, margin: '0 0.35rem' }}>·</span>
                <span style={{ color: brand.deepBlue, fontWeight: 600 }}>{tierForCount(activity).label}</span>
              </span>
            ) : <span style={{ opacity: 0.4 }}>—</span>}
          </td>
        )}
        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {usageLoading ? (
            <span style={{ color: brand.charcoal, opacity: 0.7 }}>Loading…</span>
          ) : (
            <>
              <strong style={{ color: brand.deepBlue }}>{usd(total)}</strong>
              {prior > 0 && (
                <div style={{ fontSize: '0.68rem', color: brand.charcoal, cursor: 'help' }} title={formerTitle(u.other_addresses)}>
                  {usd(u.cost_usd)} current · <span style={{ color: brand.goldenrod }}>{usd(prior)} former</span>
                </div>
              )}
            </>
          )}
        </td>
      </tr>
    );
  };

  const renderReportsTable = (list) => (
    <div style={{ background: 'white', borderRadius: 12, overflowX: 'auto', boxShadow: '0 1px 3px rgba(10,41,71,0.12)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
        <thead>
          <tr>
            <th style={th}>Name</th>
            <th style={th}>Firm</th>
            <th style={th}>Certification</th>
            <th style={th}>Usage</th>
            {rolling && <th style={th}>Activity (30d)</th>}
            <th style={{ ...th, textAlign: 'right' }}>Claude spend ({rolling ? 'last 30d' : 'MTD'})</th>
          </tr>
        </thead>
        <tbody>
          {list.map(renderReportRow)}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem' : '1.75rem 2rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
            <i className={`bi ${isManager ? 'bi-diagram-3-fill' : 'bi-person-badge-fill'}`} style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              {isManager ? 'Manager' : 'Claude Usage'}
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.25rem' : undefined }}>{isManager ? 'My Team' : 'My Usage'}</h3>
          </div>
          {me && usageConfigured !== false && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, fontSize: '0.8rem' }}>AI Usage window</span>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={rolling ? 'rolling' : 'month'}
                  onChange={e => setRolling(e.target.value === 'rolling')}
                  aria-label="AI usage window"
                  style={{
                    border: `1px solid ${brand.tealBlue}`, borderRadius: 6,
                    padding: '0.35rem 0.5rem', fontSize: '0.85rem', color: brand.deepBlue,
                    background: 'white', outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="rolling">Last 30 days</option>
                  <option value="month">Specific month</option>
                </select>
                {!rolling && (
                  <input
                    type="month"
                    value={month}
                    max={maxMonth}
                    onChange={e => e.target.value && setMonth(e.target.value)}
                    style={{
                      border: `1px solid ${brand.tealBlue}`, borderRadius: 6,
                      padding: '0.35rem 0.5rem', fontSize: '0.85rem', color: brand.deepBlue,
                      background: 'white', outline: 'none',
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Usage-not-configured notice — relevant to anyone whose usage we'd show. */}
      {me && usageQuery.data && usageConfigured === false && (
        <div style={{ background: 'rgba(208,155,44,0.12)', border: `1px solid ${brand.goldenrod}`, borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem', color: brand.charcoal, fontSize: '0.85rem' }}>
          <i className="bi bi-info-circle me-1" style={{ color: brand.goldenrod }} />
          Claude spend reporting isn&apos;t configured yet — an admin needs to set the Anthropic Analytics API key. Spend figures will show once it&apos;s in place.
        </div>
      )}
      {usageQuery.isError && (
        <div style={{ background: 'rgba(176,0,32,0.06)', border: '1px solid #b00020', color: '#b00020', borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          {usageQuery.error?.data?.detail || 'Could not load Claude usage for this period.'}
        </div>
      )}

      {isLoading ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div className="spinner-border" style={{ color: brand.tealBlue }} />
        </div>
      ) : isError ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.5rem', color: brand.goldenrod, display: 'block', marginBottom: '0.5rem' }} />
          Unable to load your team. Please try again later.
        </div>
      ) : !me ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          <i className="bi bi-person-badge" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
          We couldn&apos;t find your entry in the company directory. If that&apos;s not right, ask HR to update your directory entry.
        </div>
      ) : (
        <>
          {/* My Usage — the caller's own Claude usage, shown to everyone. */}
          <div style={{ color: brand.charcoal, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 0.5rem' }}>
            My Usage
          </div>
          <div className="row g-3" style={{ marginBottom: isManager ? '1.5rem' : 0 }}>
            {renderReportCard(me, { isSelf: true })}
          </div>

          {/* No direct reports yet — managers only. */}
          {isManager && reports.length === 0 && (
            <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
              <i className="bi bi-people" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
              No one is assigned to report to {me?.first_name || 'you'} yet. HR assigns reporting lines from Manage Directory.
            </div>
          )}

          {/* Team section — a manager with at least one report. */}
          {isManager && reports.length > 0 && (
            <>
              {/* Filter toolbar */}
              {usageConfigured !== false && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {firmOptions.length > 0 && (
                <select
                  value={firmFilter}
                  onChange={e => setFirmFilter(e.target.value)}
                  aria-label="Filter by firm"
                  title="Firm"
                  style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer' }}
                >
                  <option value="all">All firms</option>
                  {firmOptions.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              )}
              <select
                value={certFilter}
                onChange={e => setCertFilter(e.target.value)}
                aria-label="Filter by certification status"
                title="Certification status"
                style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer' }}
              >
                <option value="all">All certification</option>
                <option value="completed">Certified</option>
                <option value="pending">Pending</option>
                <option value="not_started">Not started</option>
              </select>
              <select
                value={accountFilter}
                onChange={e => setAccountFilter(e.target.value)}
                aria-label="Filter by account status"
                title="Account status"
                style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer' }}
              >
                <option value="all">Any usage</option>
                <option value="using">Active</option>
                <option value="no_account">No Usage</option>
              </select>
              {rolling && (
                <select
                  value={tierFilter}
                  onChange={e => setTierFilter(e.target.value)}
                  aria-label="Filter by usage category"
                  title="Usage category"
                  style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer' }}
                >
                  <option value="all">All categories</option>
                  {USAGE_TIERS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              )}
              {(() => {
                const shownCount = filteredReports.length + filteredIndirect.length;
                const totalCount = reports.length + indirectReports.length;
                return (
                  <span style={{ marginLeft: 'auto', color: brand.charcoal, fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Showing {shownCount === totalCount ? totalCount : `${shownCount} of ${totalCount}`}
                  </span>
                );
              })()}
              <button
                type="button"
                onClick={copyEmails}
                disabled={filteredEmails.length === 0}
                title="Copy the email addresses of the filtered reports"
                style={{ background: copied ? brand.green : brand.goldenrod, color: copied ? 'white' : brand.deepBlue, border: 'none', borderRadius: 8, padding: '0.4rem 0.7rem', fontSize: '0.85rem', fontWeight: 700, cursor: filteredEmails.length ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', opacity: filteredEmails.length ? 1 : 0.6 }}
              >
                <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} />
                {copied ? 'Copied!' : `Copy emails (${filteredEmails.length})`}
              </button>
            </div>
          )}

          {/* Usage distribution — engagement tiers by 30-day activity across the
              whole team (direct + indirect), rolling view only. Collapsed by
              default; the manager opens it with the toggle. */}
          {rolling && usageConfigured !== false && !usageLoading && (filteredReports.length + filteredIndirect.length) > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => setShowChart(s => !s)}
                aria-expanded={showChart}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.7rem', fontSize: '0.85rem', fontWeight: 700, color: brand.deepBlue, cursor: 'pointer' }}
              >
                <i className={`bi bi-chevron-${showChart ? 'down' : 'right'}`} />
                <i className="bi bi-pie-chart-fill" style={{ color: brand.goldenrod }} />
                {showChart ? 'Hide' : 'Show'} usage distribution
              </button>
              {showChart && (
                <div style={{ marginTop: '0.75rem' }}>
                  <UsageTierChart
                    counts={[...filteredReports, ...filteredIndirect].map(emp => usageByReport[emp.id]?.activity_30d || 0)}
                    title="Claude Usage Distribution"
                    subtitle="Your team (direct + indirect) grouped into engagement tiers by activity (messages + Claude Code sessions) in the last 30 days."
                  />
                </div>
              )}
            </div>
          )}

          {filteredReports.length === 0 && filteredIndirect.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
              No reports match the current filters.
            </div>
          ) : (
            <>
              {filteredReports.length > 0 && renderReportsTable(filteredReports)}

              {/* Indirect reports — everyone further down the tree, grouped under
                  whoever they report to. */}
              {filteredIndirect.length > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1.5rem 0 0.75rem' }}>
                    <span style={{ color: brand.charcoal, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                      Indirect reports
                    </span>
                    <span style={{ flex: 1, height: 1, background: brand.softGray }} />
                  </div>

                  {indirectGroups.map(group => (
                    <div key={group.manager} style={{ marginBottom: '1rem' }}>
                      <div style={{ color: brand.charcoal, fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
                        <i className="bi bi-diagram-2 me-1" style={{ color: brand.tealBlue }} />
                        Reports to <strong style={{ color: brand.deepBlue }}>{group.manager}</strong> · indirectly under you
                      </div>
                      {renderReportsTable(group.members)}
                    </div>
                  ))}
                </>
              )}
            </>
          )}

              {rolling && !usageLoading && [...filteredReports, ...filteredIndirect].some(emp => usageByReport[emp.id]?.uses_claude_code) && (
                <div style={{ marginTop: '0.6rem', color: brand.charcoal, fontSize: '0.75rem' }}>
                  <span style={{ color: brand.goldenrod, fontWeight: 800 }}>*</span> also uses Claude Code
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
